
if (!window.PlanBuilderAddons) {
  const MAX_PER_BOX = 12;

  const sameVariant = (a, b) => String(a) === String(b);

  const listFor = (deliveries, box) => {
    const delivery = deliveries[box];
    const extras = delivery && delivery.collections && delivery.collections.extras;
    return extras ? extras.selections : null;
  };

  const qtyIn = (deliveries, box, variantId) => {
    const list = listFor(deliveries, box);
    const item = list && list.find((s) => sameVariant(s.variantId, variantId));
    return item ? item.qty : 0;
  };

  const totalQty = (deliveries, variantId) =>
    deliveries.reduce((sum, _, box) => sum + qtyIn(deliveries, box, variantId), 0);

  // Absolute quantity for one product in one box; 0 removes it. Returns what
  // was stored, clamped to 0..MAX_PER_BOX.
  const setQty = (deliveries, box, meta, qty) => {
    const list = listFor(deliveries, box);
    if (!list || !meta || meta.variantId == null) return 0;
    const next = Math.max(0, Math.min(MAX_PER_BOX, Math.floor(Number(qty) || 0)));
    const idx = list.findIndex((s) => sameVariant(s.variantId, meta.variantId));
    if (next === 0) {
      if (idx >= 0) list.splice(idx, 1);
      return 0;
    }
    if (idx >= 0) {
      list[idx].qty = next;
      return next;
    }
    list.push({
      productId: String(meta.productId || ""),
      variantId: String(meta.variantId),
      sellingPlanId: meta.sellingPlanId ? String(meta.sellingPlanId) : null,
      variantPrice: Number(meta.variantPrice) || 0,
      productTitle: meta.productTitle || "",
      imageUrl: meta.imageUrl || "",
      macros: meta.macros || { kcal: 0, protein: 0, carbs: 0, fat: 0 },
      qty: next,
    });
    return next;
  };

  // The largest quantity the modal may confirm into `toBox`. `fromBox` is the
  // box being edited (null when adding). The pending quantity REPLACES the
  // edited box's units, so those are freed for the stock check; a move into a
  // box that already holds the product merges, so that box's units count
  // against the per-box cap.
  const maxPending = (deliveries, { variantId, variantMax, fromBox, toBox }) => {
    const stock = Number.isFinite(variantMax) ? variantMax : Infinity;
    const fromQty = fromBox == null ? 0 : qtyIn(deliveries, fromBox, variantId);
    const targetExisting = toBox === fromBox ? 0 : qtyIn(deliveries, toBox, variantId);
    const byStock = stock - (totalQty(deliveries, variantId) - fromQty);
    const byBox = MAX_PER_BOX - targetExisting;
    return Math.max(0, Math.min(byStock, byBox));
  };

  // Applies the modal's confirm. Add merges into toBox. Edit replaces the edited
  // box's quantity, or moves it out of fromBox and merges into toBox.
  const confirm = (deliveries, { meta, fromBox, toBox, qty }) => {
    const pending = Math.max(0, Math.floor(Number(qty) || 0));
    if (fromBox == null) {
      return setQty(deliveries, toBox, meta, qtyIn(deliveries, toBox, meta.variantId) + pending);
    }
    if (toBox === fromBox) return setQty(deliveries, toBox, meta, pending);
    setQty(deliveries, fromBox, meta, 0);
    return setQty(deliveries, toBox, meta, qtyIn(deliveries, toBox, meta.variantId) + pending);
  };

  const assignments = (deliveries, variantId) =>
    deliveries
      .map((_, box) => ({ box, qty: qtyIn(deliveries, box, variantId) }))
      .filter((a) => a.qty > 0);

  const lines = (deliveries, box) =>
    (listFor(deliveries, box) || []).map((s) => ({
      ...s,
      box,
      lineCents: (Number(s.variantPrice) || 0) * s.qty,
    }));

  const totalCents = (deliveries) =>
    deliveries.reduce(
      (sum, _, box) => sum + lines(deliveries, box).reduce((boxSum, l) => boxSum + l.lineCents, 0),
      0
    );

  // Units, not lines: two of one Add On, or one in each box, both count as 2.
  // The cart review summary counts the same way (cart-review-summary.liquid).
  const totalUnits = (deliveries) =>
    deliveries.reduce(
      (sum, _, box) => sum + lines(deliveries, box).reduce((boxSum, l) => boxSum + l.qty, 0),
      0
    );

  // buildRechargeBundle() silently skips selections without a selling plan,
  // which would show the customer a price and never charge it.
  const missingSellingPlan = (deliveries) =>
    deliveries.flatMap((_, box) => lines(deliveries, box)).filter((l) => !l.sellingPlanId);

  // Marks the Recharge cart lines for ONE box. `items` is what
  // getDynamicBundleItems() returned for that box; each item's `id` is the
  // variant id. Mutates and returns the array.
  const tagCheckoutLines = (items, deliveries, box, isoDate) => {
    const ids = new Set(lines(deliveries, box).map((l) => String(l.variantId)));
    (items || []).forEach((item) => {
      const variantId = item.id != null ? item.id : item.variant_id;
      if (!ids.has(String(variantId))) return;
      const extra = { _cr_addon: "1" };
      if (isoDate) extra._delivery_date = isoDate;
      item.properties = Object.assign({}, item.properties, extra);
    });
    return items;
  };

  window.PlanBuilderAddons = {
    MAX_PER_BOX,
    qtyIn,
    totalQty,
    setQty,
    maxPending,
    confirm,
    assignments,
    lines,
    totalCents,
    totalUnits,
    missingSellingPlan,
    tagCheckoutLines,
  };
}
